import React from 'react'
import { Button, ButtonStyle } from '../../../components/simple/button'
import { Teacher } from '../../../providers/context' // Import the context
import { Assessment } from '../assessmentDashboard'
import { makeRequest, showAxiosErrorAsToast, useRequest } from '../../../request'
import { inputFiles, getPresignedUrl, uploadToS3 } from '../../../components/media/fileUpload'
import { Popups } from '../../../providers/popups'
import { LocalTable } from '../../../components/table'
import { TextInput } from '../../../components/form/textInput'
import { FloatingLoader } from '../../../components/simple/loader'
import { Divider } from '../../../components/simple/divider'
import { Toasts } from '../../../providers/toast'
import { InstructionPanel } from '../../../components/simple/instruction'
import { useNavigate } from 'react-router-dom'

export const AdditionalDataTab: React.FC<{
  assessment: Assessment
  user: Teacher
}> = ({ assessment, user }) => {
  const navigate = useNavigate()
  return (
    <div className={`mt-4 flex flex-col items-stretch gap-2 self-center`}>
      <div className="rounded bg-warning p-2">
        This assessment needs <b>{assessment.needsAdditionalData?.join(', ')}</b> for each student. You can upload
        matching data files here.
      </div>
      <InstructionPanel>
        You must upload a CSV file with the following headers.
        <pre className="my-1 overflow-x-auto rounded bg-gray-100 p-2">
          {`student_id,${assessment.needsAdditionalData?.join(',')}`}
        </pre>
        There should be one row for each student.
        <ul className="my-2 list-disc pl-5">
          <li>
            <code>student_id</code> - matches student ID in your system
          </li>
          {assessment.needsAdditionalData?.includes('state_id') && (
            <li>
              <code>state_id</code> - student's state ID number
            </li>
          )}
          {assessment.needsAdditionalData?.includes('birthdate') && (
            <li>
              <code>birthdate</code> - student's birthdate (MM/DD/YYYY)
            </li>
          )}
        </ul>
      </InstructionPanel>
      <Divider className="my-4" />
      <AdditionalDataPanel user={user} onContinue={() => navigate('#process')} />
    </div>
  )
}

type MatchingData = {
  syncID: string
  name: string
  stateID: string
  birthdate: string
}

export const AdditionalDataPanel: React.FC<{
  user: Teacher
  onDataChange?: () => void
  onContinue?: () => void
}> = ({ user, onDataChange, onContinue }) => {
  const { data: matchingData, loading, reload } = useRequest<MatchingData[]>('/frontend_api/getAllMatchingData')
  const [uploading, setUploading] = React.useState(false)
  const [clearing, setClearing] = React.useState(false)
  const [search, setSearch] = React.useState('')

  const uploadMatchingData = async () => {
    setUploading(true)
    const files = await inputFiles('csv')
    let uploaded = 0
    for (const file of files || []) {
      try {
        const url = await getPresignedUrl(file.name)
        await uploadToS3(url, file)
        const { status, message } = await makeRequest<{
          status: 'success' | 'error'
          message: string
        }>('/frontend_api/uploadMatchingData', {
          fileName: file.name,
        })
        Toasts.showToast({
          subject: file.name,
          message: message,
          error: status === 'error',
          duration: 120000,
        })
        uploaded++
      } catch (e) {
        showAxiosErrorAsToast(e)
      }
    }
    if (uploaded) {
      await reload()
      onDataChange?.()
    }
    setUploading(false)
  }

  const clearMatchingData = async () => {
    if (
      !(await Popups.simpleConfirm(
        'Are you sure you want to clear all matching data? This affects all of your schools, not just this assessment'
      ))
    )
      return

    setClearing(true)
    try {
      await makeRequest('/frontend_api/clearMatchingData')
      onDataChange?.()
    } catch (e) {
      showAxiosErrorAsToast(e)
    } finally {
      setClearing(false)
    }
    await reload()
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row items-center justify-between gap-8">
        <Button
          buttonStyle={ButtonStyle.blue}
          showLoader={uploading}
          disabled={uploading || clearing || loading}
          onClick={uploadMatchingData}
        >
          Upload Matching Data
        </Button>
        <div className="text-xl font-semibold">{user.districtAccess ? 'District' : 'School'} Matching Data</div>
        <Button
          buttonStyle={ButtonStyle.ghostRed}
          showLoader={clearing}
          disabled={uploading || clearing || loading}
          onClick={clearMatchingData}
        >
          Clear All
        </Button>
      </div>
      <div className={`relative mt-2 flex flex-col gap-2 ${loading ? 'pointer-events-none opacity-50' : ''}`}>
        {loading && <FloatingLoader />}
        <TextInput className="self-end" value={search} onChange={setSearch} placeholder="Search" />
        <LocalTable
          columns={[
            {
              header: 'Name',
              accessorKey: 'name',
            },
            {
              header: 'Student ID',
              accessorKey: 'syncID',
            },
            {
              header: 'State ID',
              accessorKey: 'stateID',
            },
            {
              header: 'Birthdate',
              accessorKey: 'birthdate',
            },
          ]}
          data={matchingData || []}
          pagination={10}
          search={search}
        />
      </div>
      <Button
        className="self-start"
        logo
        disabled={matchingData?.length === 0 || uploading || clearing || loading}
        onClick={() => onContinue?.()}
      >
        Continue
      </Button>
    </div>
  )
}
